import { useMemo } from 'react';
import type { Character, MoveRef } from '@/schemas/character';
import type { Move } from '@/schemas/move';
import { getActiveSpellGrantsForLevelUpMany } from '@/services/spellGrants';
import { getActiveSpellLists } from '@/services/spellAccess';
import { buildJustAcquiredMoves } from './justAcquired';
import type { StepFlowFlags } from './stepRouting';
import type { LevelUpDraft } from './types';

/**
 * Hook: calcola i 5 flag `needs*Step` del `LevelUpFlow`. Estratto da
 * `LevelUpFlowPage.tsx` (PR 10) per rispettare `max-lines: 400`.
 */
export function useFlowFlags(
  character: Character,
  draft: LevelUpDraft,
  nextLevel: number,
  moveDef: Move | undefined,
): StepFlowFlags {
  const needsArchetypeStep = draft.moveAdded?.id === 'common.infanzia-complessa';
  const needsGrantedStep = moveDef?.grantsMoveChoice !== undefined;
  const needsMulticlassStep = moveDef?.multiclass !== undefined;
  const needsPastTenStep = nextLevel === 11 && character.pastTen === undefined;
  const needsSpellsStep = useNeedsSpellsStep(
    character,
    draft.moveAdded,
    draft.grantedChoice,
    draft.multiclassPick,
  );
  return {
    needsArchetypeStep,
    needsGrantedStep,
    needsMulticlassStep,
    needsPastTenStep,
    needsSpellsStep,
  };
}

/**
 * Il LevelUpFlow ha bisogno dello step «Spell»? Sì se almeno una di
 * queste 2 condizioni: le mosse appena acquisite hanno grant pick attive
 * (Mago Corpus Arcano, mosse `on-take` come Magnus/Tomo/Rivelazioni, Vincolo
 * del Tomo via grantedChoice) o il PG è casteggiante tradizione-native.
 *
 * **FI-35 fix Gap D**: considera anche `grantedChoice` (sotto-scelta D5).
 */
function useNeedsSpellsStep(
  character: Character,
  moveAdded: MoveRef | undefined,
  grantedChoice: LevelUpDraft['grantedChoice'],
  multiclassPick: LevelUpDraft['multiclassPick'],
): boolean {
  return useMemo<boolean>(() => {
    if (moveAdded === undefined) return false;
    const justAcquired = buildJustAcquiredMoves(
      character.classId,
      moveAdded,
      grantedChoice,
      multiclassPick,
    );
    const grants = getActiveSpellGrantsForLevelUpMany(
      character.classId,
      character.moves,
      justAcquired,
    );
    if (grants.length > 0) return true;
    const lists = getActiveSpellLists([...character.moves, ...justAcquired]);
    return lists.some(
      (l) =>
        l.mode === 'chierico-native' || l.mode === 'druido-native' || l.mode === 'paladino-native',
    );
  }, [character.classId, character.moves, moveAdded, grantedChoice, multiclassPick]);
}
